import {ChainedCommands} from '@tiptap/core';
import {Editor} from '@tiptap/react';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import TextEditorButton from '../../../../incase-domain/components/textEditor/TextEditorButton';
import {LinkEditorButton} from './components/LinkEditorButton';
import {applyTextStyleCommand} from './textStyleCommands';
import {useEditorTransaction} from './useEditorTransaction';
import './textStyleToolbar.scss';

interface TextStyleToolbarProps {
    editor: Editor;
    compact?: boolean;
}

type ToolbarAction = {
    icon: string;
    labelKey:
        | 'textStyleToolbar.heading1'
        | 'textStyleToolbar.heading2'
        | 'textStyleToolbar.heading3'
        | 'textStyleToolbar.heading4'
        | 'textStyleToolbar.bold'
        | 'textStyleToolbar.italic'
        | 'textStyleToolbar.underline'
        | 'textStyleToolbar.strike'
        | 'textStyleToolbar.subscript'
        | 'textStyleToolbar.superscript'
        | 'textStyleToolbar.clearFormatting'
        | 'textStyleToolbar.alignLeft'
        | 'textStyleToolbar.alignCenter'
        | 'textStyleToolbar.alignRight'
        | 'textStyleToolbar.alignJustify'
        | 'textStyleToolbar.bulletList'
        | 'textStyleToolbar.orderedList';
    isActive?: () => boolean;
    command: (chain: ChainedCommands) => ChainedCommands;
};

function TextStyleToolbar({editor, compact = false}: TextStyleToolbarProps): JSX.Element {
    const {t} = useTranslation();
    useEditorTransaction(editor);

    const apply = (command: (chain: ChainedCommands) => ChainedCommands): void => {
        applyTextStyleCommand(editor, command);
    };

    const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const renderGroup = (actions: ToolbarAction[]): JSX.Element => (
        <div className="c-textStyleToolbar__group">
            {actions.map((action) => (
                <TextEditorButton
                    key={action.labelKey}
                    icon={action.icon}
                    size={compact ? 'md' : 'sm'}
                    className="c-textStyleToolbar__button"
                    ariaLabel={t(action.labelKey)}
                    isActive={action.isActive?.()}
                    onClick={() => apply(action.command)}
                />
            ))}
        </div>
    );

    const headingActions: ToolbarAction[] = [
        {
            icon: 'ph:text-h-one',
            labelKey: 'textStyleToolbar.heading1',
            isActive: () => editor.isActive('heading', {level: 1}),
            command: (c) => c.toggleHeading({level: 1}),
        },
        {
            icon: 'ph:text-h-two',
            labelKey: 'textStyleToolbar.heading2',
            isActive: () => editor.isActive('heading', {level: 2}),
            command: (c) => c.toggleHeading({level: 2}),
        },
        {
            icon: 'ph:text-h-three',
            labelKey: 'textStyleToolbar.heading3',
            isActive: () => editor.isActive('heading', {level: 3}),
            command: (c) => c.toggleHeading({level: 3}),
        },
        {
            icon: 'ph:text-h-four',
            labelKey: 'textStyleToolbar.heading4',
            isActive: () => editor.isActive('heading', {level: 4}),
            command: (c) => c.toggleHeading({level: 4}),
        },
    ];

    const markActions: ToolbarAction[] = [
        {
            icon: 'ph:text-bolder-bold',
            labelKey: 'textStyleToolbar.bold',
            isActive: () => editor.isActive('bold'),
            command: (c) => c.toggleBold(),
        },
        {
            icon: 'ph:text-italic',
            labelKey: 'textStyleToolbar.italic',
            isActive: () => editor.isActive('italic'),
            command: (c) => c.toggleItalic(),
        },
        {
            icon: 'ph:text-underline',
            labelKey: 'textStyleToolbar.underline',
            isActive: () => editor.isActive('underline'),
            command: (c) => c.toggleUnderline(),
        },
        {
            icon: 'ph:text-strikethrough',
            labelKey: 'textStyleToolbar.strike',
            isActive: () => editor.isActive('strike'),
            command: (c) => c.toggleStrike(),
        },
        {
            icon: 'ph:text-subscript',
            labelKey: 'textStyleToolbar.subscript',
            isActive: () => editor.isActive('subscript'),
            command: (c) => c.toggleSubscript(),
        },
        {
            icon: 'ph:text-superscript',
            labelKey: 'textStyleToolbar.superscript',
            isActive: () => editor.isActive('superscript'),
            command: (c) => c.toggleSuperscript(),
        },
    ];

    const alignActions: ToolbarAction[] = [
        {
            icon: 'ph:text-align-left',
            labelKey: 'textStyleToolbar.alignLeft',
            isActive: () => editor.isActive({textAlign: 'left'}),
            command: (c) => c.setTextAlign('left'),
        },
        {
            icon: 'ph:text-align-center',
            labelKey: 'textStyleToolbar.alignCenter',
            isActive: () => editor.isActive({textAlign: 'center'}),
            command: (c) => c.setTextAlign('center'),
        },
        {
            icon: 'ph:text-align-right',
            labelKey: 'textStyleToolbar.alignRight',
            isActive: () => editor.isActive({textAlign: 'right'}),
            command: (c) => c.setTextAlign('right'),
        },
        {
            icon: 'ph:text-align-justify',
            labelKey: 'textStyleToolbar.alignJustify',
            isActive: () => editor.isActive({textAlign: 'justify'}),
            command: (c) => c.setTextAlign('justify'),
        },
    ];

    const listActions: ToolbarAction[] = [
        {
            icon: 'ph:list-bullets',
            labelKey: 'textStyleToolbar.bulletList',
            isActive: () => editor.isActive('bulletList'),
            command: (c) => c.toggleBulletList(),
        },
        {
            icon: 'ph:list-numbers',
            labelKey: 'textStyleToolbar.orderedList',
            isActive: () => editor.isActive('orderedList'),
            command: (c) => c.toggleOrderedList(),
        },
    ];

    return (
        <div
            className={`c-textStyleToolbar${compact ? ' c-textStyleToolbar--compact' : ''}`}
            data-testid="text-style-toolbar"
            onMouseDown={handleMouseDown}
            onClick={(event) => event.stopPropagation()}
        >
            {renderGroup(headingActions)}
            {renderGroup(markActions)}
            {renderGroup(alignActions)}
            {renderGroup(listActions)}
            <div className="c-textStyleToolbar__group">
                <LinkEditorButton editor={editor} size={compact ? 'md' : 'sm'} />
            </div>
            {renderGroup([
                {
                    icon: 'solar:eraser-linear',
                    labelKey: 'textStyleToolbar.clearFormatting',
                    command: (c) => c.clearNodes().unsetAllMarks(),
                },
            ])}
        </div>
    );
}

export default TextStyleToolbar;
